import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, FileText, ChevronRight, ChevronDown, ChevronUp, Loader2, Check, CornerUpLeft } from 'lucide-react';
import StatCard from '../shared/StatCard';
import StatusStatRow from '../shared/StatusStatRow';
import DateRangeFilter from '../shared/DateRangeFilter';
import ProjectFilter from '../shared/ProjectFilter';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, selectStyle } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL, PAYMENT_METHODS } from '../../../../shared/constants';
import client from '../../api/client';
import AuthedReceiptPreview from '../../shared/components/AuthedReceiptPreview';

const STATUS_ORDER = ['pending_review', 'approved', 'paid', 'pending', 'partially_approved', 'sent_back', 'partially_returned', 'rejected'];

const SECTION_LABELS = {
  pending_review:       'Pendiente de Revisión',
  approved:             'Aprobado',
  paid:                 'Pagado',
  pending:              'En Revisión (Supervisor)',
  partially_approved:   'Parcialmente Aprobado',
  sent_back:            'Devuelto',
  partially_returned:   'Parcialmente Devuelto',
  rejected:             'Rechazado',
};

function fmt(amount) {
  if (amount === null || amount === undefined) return '—';
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: '600',
      whiteSpace: 'nowrap',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function ReviewerListPage({ employees }) {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [employeeId, setEmployeeId] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [sectionExpanded, setSectionExpanded] = useState({
    pending_review: true, approved: false, paid: false, pending: false,
    partially_approved: false, sent_back: false, partially_returned: false, rejected: false,
  });

  const fetchReceipts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (employeeId) params.employeeId = employeeId;
      // Fetch all non-draft receipts for the reviewer
      const res = await client.get('/api/receipts', { params });
      setReceipts(res.data);
    } catch (err) {
      console.error('Failed to load receipts:', err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, employeeId]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  // Filter receipts by project and group by status
  const filteredReceipts = useMemo(() => {
    let filtered = receipts;
    if (selectedProjects.length > 0) {
      filtered = filtered.filter(r =>
        (r.items || []).some(i => selectedProjects.includes(i.projectName))
      );
    }
    return filtered;
  }, [receipts, selectedProjects]);

  const receiptsByStatus = useMemo(() => {
    const grouped = {};
    for (const r of filteredReceipts) {
      const st = r.displayStatus || r.status;
      if (!grouped[st]) grouped[st] = [];
      grouped[st].push(r);
    }
    return grouped;
  }, [filteredReceipts]);

  // Stats
  const pendingReview = receipts.filter(r => r.status === 'pending_review');
  const approved = receipts.filter(r => r.status === 'approved');
  const approvedAmt = approved.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  // All project names for filter
  const allProjectNames = useMemo(() => {
    const names = new Set();
    receipts.forEach(r => (r.items || []).forEach(i => { if (i.projectName) names.add(i.projectName); }));
    return [...names].sort();
  }, [receipts]);

  function handleDateChange({ dateFrom: df, dateTo: dt }) {
    setDateFrom(df);
    setDateTo(dt);
  }

  // Step indicator for each receipt
  function getStepLabel(status) {
    switch (status) {
      case 'pending': return 'En revisión por supervisor';
      case 'partially_approved': return 'En revisión por supervisor (parcial)';
      case 'pending_review': return 'Listo para revisión';
      case 'approved': return 'Aprobado';
      case 'sent_back': return 'Devuelto';
      case 'partially_returned': return 'Parcialmente devuelto';
      case 'rejected': return 'Rechazado';
      default: return status;
    }
  }

  const renderReceiptCard = (receipt) => {
    const isPendingReview = receipt.status === 'pending_review';
    const projectNames = [...new Set((receipt.items || []).map(i => i.projectName).filter(Boolean))];

    return (
      <div
        key={receipt.id}
        onClick={() => navigate(`/supervisor/receipt/${receipt.id}`)}
        style={{
          ...bubbleStyle,
          padding: '14px 18px',
          cursor: 'pointer',
          transition: 'box-shadow 0.15s, transform 0.1s',
          borderLeft: isPendingReview ? `3px solid #5C6BC0` : undefined,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '';
          e.currentTarget.style.transform = '';
        }}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          {/* Preview thumbnail */}
          <div style={{
            width: 48, height: 48, borderRadius: 8, overflow: 'hidden',
            flexShrink: 0, background: '#f1f5f9', border: `1px solid ${BORDER_COLOR}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {receipt.previewUrl && receipt.fileType?.startsWith('image/') ? (
              <AuthedReceiptPreview src={receipt.previewUrl} fileType={receipt.fileType} alt="receipt" imgStyle={{ width: '100%', height: '100%', objectFit: 'cover' }} style={{ width: '100%', height: '100%' }} />
            ) : (
              <FileText size={20} style={{ color: '#94a3b8' }} />
            )}
          </div>

          {/* Receipt info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '700', fontSize: 15, color: '#1e293b' }}>
                {receipt.receiptNumber ? `#${receipt.receiptNumber}` : '(Sin #)'}
                {receipt.invoiceNumber ? ` · Factura: ${receipt.invoiceNumber}` : ''}
                {' — '}
                {receipt.vendor || '(Sin proveedor)'}
              </span>
              <StatusBadge status={receipt.displayStatus || receipt.status} />
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {receipt.employeeName && <span>{receipt.employeeName}</span>}
              {receipt.date && <span>| {receipt.date}</span>}
              {receipt.paymentMethod && <span>| {PAYMENT_METHODS[receipt.paymentMethod] || receipt.paymentMethod}</span>}
              {projectNames.length > 0 && <span>| {projectNames.join(', ')}</span>}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontStyle: 'italic' }}>
              {getStepLabel(receipt.status)}
            </div>
          </div>

          {/* Total + chevron */}
          <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontWeight: '700', fontSize: 18, color: '#1e293b' }}>
              {fmt((receipt.items || []).length > 0
                ? (receipt.items || []).reduce((s, i) => { const a=parseFloat(i.amount)||0; return s+a+a*(parseFloat(i.taxRate)||0)/100; }, 0)
                : receipt.amount)}
            </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{(receipt.items || []).length} item{(receipt.items || []).length !== 1 ? 's' : ''}</div>
            </div>
            <ChevronRight size={18} style={{ color: '#94a3b8' }} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, color: '#94a3b8', gap: 8 }}>
          <Loader2 size={20} className="spin" /> Cargando recibos…
        </div>
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: '0 0 18px' }}>
        Revisión de Gastos
      </h2>

      {/* Stats */}
      {/* 🔴 V65 — fila derivada. Era la peor de las tres: dos tarjetas contra un Total de
          `receipts.filter(s !== 'draft')`, así que faltaban DEVUELTOS, PAGADOS y
          RECHAZADOS y el total los incluía igual. `excluir` deja los borradores fuera del
          universo ENTERO —total y tarjetas—, que es distinto de restarlos solo del total. */}
      <StatusStatRow
        items={receipts}
        excluir={['draft']}
        estadoDe={(r) =>
            ['sent_back', 'partially_returned'].includes(r.status) ? 'sent_back'
          : ['pending', 'partially_approved'].includes(r.status) ? 'pending'
          : r.status}
        orden={['pending_review', 'pending', 'sent_back', 'approved', 'rejected', 'paid']}
        etiquetas={{ pending_review: 'Por Revisar', pending: 'En Revisión',
                     sent_back: 'Devueltos', approved: 'Aprobados',
                     rejected: 'Rechazados', paid: 'Pagados' }}
        subtitulos={{ approved: fmt(approvedAmt) }}
        destacado="pending_review"
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} />
        {employees.length > 0 && (
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            style={{
              ...selectStyle, width: 'auto', minWidth: 160,
              height: 34, paddingTop: 0, paddingBottom: 0, fontSize: 13,
            }}
          >
            <option value="">Todos los Choferes</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        )}
        <ProjectFilter projects={allProjectNames} selected={selectedProjects} onChange={setSelectedProjects} />
      </div>

      {filteredReceipts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
          <ClipboardCheck size={44} style={{ marginBottom: 12, color: '#cbd5e1' }} />
          <div style={{ fontSize: 16, fontWeight: '600', color: '#64748b' }}>
            No hay recibos
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {STATUS_ORDER.map(status => {
            const sectionReceipts = receiptsByStatus[status];
            if (!sectionReceipts || sectionReceipts.length === 0) return null;
            const expanded = sectionExpanded[status] ?? false;
            const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
            const Icon = expanded ? ChevronUp : ChevronDown;
            return (
              <div key={status}>
                <button
                  onClick={() => setSectionExpanded(prev => ({ ...prev, [status]: !prev[status] }))}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    padding: '12px 16px', background: '#fff',
                    border: `1px solid ${BORDER_COLOR}`,
                    borderRadius: expanded ? '12px 12px 0 0' : 12,
                    cursor: 'pointer', fontFamily: 'Inter, sans-serif', minHeight: 48,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.border, flexShrink: 0, display: 'inline-block' }} />
                    <span style={{ fontWeight: '600', fontSize: 14, color: NAVY }}>
                      {SECTION_LABELS[status] || STATUS_LABEL[status] || status}
                    </span>
                  </div>
                  <span style={{
                    background: '#f1f5f9', color: '#64748b', borderRadius: 20,
                    padding: '2px 10px', fontSize: 12, fontWeight: '500', marginRight: 12,
                  }}>
                    {sectionReceipts.length} recibo{sectionReceipts.length !== 1 ? 's' : ''}
                  </span>
                  <Icon size={16} style={{ color: '#94a3b8', flexShrink: 0 }} />
                </button>
                {expanded && (
                  <div style={{
                    border: `1px solid ${BORDER_COLOR}`, borderTop: 'none',
                    borderRadius: '0 0 12px 12px', background: '#fafbfc', padding: '10px 10px',
                    display: 'flex', flexDirection: 'column', gap: 10,
                  }}>
                    {sectionReceipts.map(receipt => renderReceiptCard(receipt))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
